/**
 * 로케일 Provider.
 *
 * 비컴포넌트 export(useT·detectLocale)는 context.ts 에 있다 — 한 .tsx 에서 컴포넌트와
 * 비컴포넌트를 함께 export 하면 react-refresh 규칙이 파일 전체를 경고한다.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { MESSAGES, type Locale } from './messages';
import {
  I18nContext,
  I18N_STORAGE_KEY,
  detectLocale,
  type I18nValue,
} from './context';

function readStored(): string | null {
  // localStorage 는 프라이빗 모드·설정에 따라 접근 자체가 throw 한다
  try {
    return localStorage.getItem(I18N_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function I18nProvider({
  children,
  initialLocale,
}: {
  children: ReactNode;
  /** 테스트에서 로케일을 고정할 때 쓴다 */
  initialLocale?: Locale;
}) {
  const [locale, setLocaleState] = useState<Locale>(
    () =>
      initialLocale ??
      detectLocale(readStored(), typeof navigator === 'undefined' ? [] : navigator.languages ?? [])
  );

  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try {
      localStorage.setItem(I18N_STORAGE_KEY, l);
    } catch {
      // 저장에 실패해도 이번 세션의 전환은 유지한다
    }
  }, []);

  const value = useMemo<I18nValue>(
    () => ({ locale, setLocale, t: MESSAGES[locale] }),
    [locale, setLocale]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
